import { useMutation, useQueryClient } from 'react-query';
import { postWithHeaders } from 'Core/utils/platformApi';

import cacheKeys from 'Core/hooks/queries/cacheKeys';
import useAlertStore, { clearAlertSelector, setErrorAlertSelector } from 'Core/stores/alert';
import { useTranslation } from 'react-i18next';
import { features, postLogMessage } from 'Core/utils/logger';
import { getUrlParam } from 'Core/utils/utils';
import { useGetPaymentInfo } from 'Core/hooks/platform/useGetPaymentInfo';

function transformPayload({
  customer_id,
  customer_identity_number,
  external_reference_id,
  purpose,
  provider,
  payment_option,
}) {
  return {
    payment_id: getUrlParam('payment-id'),
    external_reference_id,
    purpose,
    payment_option,
    provider,
    payload: {
      customer_id,
      customer_identity_number,
    },
  };
}

export default function useRequalifyApplication() {
  const { t } = useTranslation();
  const setErrorAlert = useAlertStore(setErrorAlertSelector);
  const clearAlert = useAlertStore(clearAlertSelector);
  const { data: paymentInfo } = useGetPaymentInfo();
  const queryClient = useQueryClient();

  return useMutation(
    (payload) => {
      return postWithHeaders(
        `/financing/application-requalify`,
        transformPayload({
          ...payload,
          customer_id: paymentInfo.customer_id,
          external_reference_id: paymentInfo.external_reference_id,
          purpose: paymentInfo.purpose,
          provider: paymentInfo.provider,
          payment_option: paymentInfo.payment_option,
        })
      );
    },
    {
      throwOnError: true,
      retry: 2,
      enabled: Boolean(paymentInfo),
      onSuccess(data) {
        postLogMessage({
          feature: features.requalifyApp,
          status: 'success',
        });

        clearAlert();
        queryClient.invalidateQueries(cacheKeys.financingPlatformApplication);
        queryClient.invalidateQueries(cacheKeys.financingNextStep);

        return data;
      },
      onError(error) {
        setErrorAlert(
          t('status.error.requalifyApplicationRequestFailed') +
            `(${error?.response?.data?.message})`
        );
      },
    }
  );
}
